import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import timetableService from "../../services/timetableService";
import { queryKeys } from "./queryKeys";
import { unwrapTimetableRecordFromQueryResult } from "../../utils/timetableScheduleFromDetail";

// ==================== QUERY HOOKS ====================

export const useTimetable = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.timetable.all, filters],
    queryFn: () => timetableService.getAllTimetables(filters),
    staleTime: 5 * 60 * 1000,
  });
};

export const useTimetableFilterOptions = () => {
  return useQuery({
    queryKey: [...queryKeys.timetable.all, "filter-options"],
    queryFn: () => timetableService.getFilterOptions(),
    staleTime: 10 * 60 * 1000,
  });
};

export const useTimetableById = (id) => {
  return useQuery({
    queryKey: queryKeys.timetable.detail(id),
    queryFn: async () => {
      const res = await timetableService.getTimetableById(id);
      return unwrapTimetableRecordFromQueryResult(res);
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes - balanced for updates but prevents blank screens
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useClassTimetable = (classId) => {
  return useQuery({
    queryKey: queryKeys.timetable.class(classId),
    queryFn: () => timetableService.getClassTimetable(classId),
    enabled: !!classId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTeacherTimetable = (teacherId, params) => {
  return useQuery({
    queryKey: [...queryKeys.timetable.teacher(teacherId), params || {}],
    queryFn: () => timetableService.getTeacherTimetable(teacherId, params),
    enabled: !!teacherId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useStudentTimetable = (studentId) => {
  return useQuery({
    queryKey: queryKeys.timetable.student(studentId),
    queryFn: () => timetableService.getStudentTimetable(studentId),
    enabled: !!studentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTimetableAnalytics = (id) => {
  return useQuery({
    queryKey: queryKeys.timetable.analytics(id),
    queryFn: () => timetableService.getTimetableAnalytics(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// ==================== MUTATION HOOKS ====================

export const useGenerateTimetable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params) => timetableService.generateTimetable(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const usePreviewTimetable = () => {
  return useMutation({
    mutationFn: (params) => timetableService.previewTimetable(params),
  });
};

export const useToggleTimetableStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, params }) => 
      timetableService.toggleTimetableStatus(id, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.detail(variables.id),
      });
    },
  });
};

export const useUpdateTimetableMeta = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }) => timetableService.updateTimetableMeta(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.detail(variables.id),
      });
    },
  });
};

export const useLockTimetable = () => {
  return useMutation({
    mutationFn: (id) => timetableService.lockTimetable(id),
  });
};

export const useUnlockTimetable = () => {
  return useMutation({
    mutationFn: (id) => timetableService.unlockTimetable(id),
  });
};

export const useDeleteTimetable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => timetableService.deleteTimetable(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useRegenerateTimetable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, params }) =>
      timetableService.regenerateTimetable(id, {
        ...params,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.detail(variables.id),
      });
    },
  });
};

export const useCreateTimetableFromScratch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => timetableService.createTimetableFromScratch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useCreateSlot = (timetableId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryData) => timetableService.createSlot(timetableId, entryData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.detail(timetableId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.timetable.detail(timetableId),
      });
    },
  });
};

export const useUpdateSlot = (timetableId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slotId, entryData }) =>
      timetableService.updateSlot(timetableId, slotId, entryData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.detail(timetableId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.timetable.detail(timetableId),
      });
    },
  });
};

export const useDeleteSlot = (timetableId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (slotId) => timetableService.deleteSlot(timetableId, slotId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.detail(timetableId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.timetable.detail(timetableId),
      });
    },
  });
};

export const useValidateSlot = (timetableId) => {
  return useMutation({
    mutationFn: (slotData) => timetableService.validateSlot(timetableId, slotData),
  });
};

export const useExportTimetable = () => {
  return useMutation({
    mutationFn: ({ id, format }) => 
      timetableService.exportTimetable(id, format),
  });
};

// ==================== LEGACY HOOKS ====================

export const useTimetableByDate = (date) => {
  return useQuery({
    queryKey: queryKeys.timetable.byDate(date),
    queryFn: () => timetableService.getTimetable({ date }),
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
  });
};

export const useScheduleChangeRequests = (filters = {}) => {
  return useQuery({
    queryKey: queryKeys.schedule.changes(),
    queryFn: () => timetableService.getScheduleChangeRequests(filters),
    staleTime: 2 * 60 * 1000,
  });
};

export const useCreateTimetableEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryData) =>
      timetableService.createTimetableEntry(entryData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useUpdateTimetableEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, entryData }) =>
      timetableService.updateTimetableEntry(id, entryData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useDeleteTimetableEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => timetableService.deleteTimetableEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useRequestScheduleChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestData) =>
      timetableService.requestScheduleChange(requestData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule.changes(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useApproveScheduleChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId) =>
      timetableService.approveScheduleChange(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule.changes(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useRejectScheduleChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, reason }) =>
      timetableService.rejectScheduleChange(requestId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule.changes(),
      });
    },
  });
};

export const useUpdateScheduleChangeRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }) =>
      timetableService.updateScheduleChangeRequest(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule.changes(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.all,
      });
    },
  });
};

export const useCancelScheduleChangeRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => timetableService.cancelScheduleChangeRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule.changes(),
      });
    },
  });
};

export const useDownloadTimetable = () => {
  return useMutation({
    mutationFn: (classId) => timetableService.downloadTimetable(classId),
  });
};

